import React, { useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import FormLabel from '../../form-label/form-label'
import FormRow from '../../form-row/form-row'
import Button from 'pobble-components/dist/button'
import { apiEmailSignUp } from '../../../common/api/email-identity'
import { errorHandler } from 'pobble-components/dist/error-handler'
import EVENTS from '../../../events'
import { Link, useLocation } from 'react-router-dom'
import Auth from '../../auth/auth'
import { Oauth } from '../../oauth/oauth'
import { useOrigin } from '../../../common/utils'

const Signup = injectSheet(({ FONT_SIZES, COLORS }) => ({
  container: {
    background: '#FAFAFA',
    minHeight: '100vh',
    padding: '24px',
    alignItems: 'center'
  },
  intro: {
    textAlign: 'center'
  },
  introTitle: {
    marginBottom: 0
  },
  introDesc: {
    fontSize: '16px',
    margin: '8px 0 34px'
  },
  divider: {
    margin: '0',
    position: 'relative',
    height: '1px',
    background: '#ECEDEA',
    font: 'var(--body--)',
    '& span': {
      position: 'absolute',
      left: '50%',
      top: '0',
      margin: '-14px 0 0 -10px',
      padding: '6px',
      background: '#fff'
    }
  },
  layoutForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '400px',
    width: '100%'
  },
  signupBox: {
    width: '100%',
    textAlign: 'center',
    borderRadius: '8px',
    padding: '48px 48px 0',
    fontSize: '16px',
    margin: '0 0 20px',
    background: '#fff',
    boxShadow: '0 20px 25px rgba(0, 0, 0, .1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '48px'
  },
  form: {
    '& button': {
      background: 'var(--dark-blue)',
      border: 'none',
      color: '#fff',
      '&:hover, &:focus': {
        background: 'var(--dark-blue-)'
      }
    }
  },
  error: {
    font: 'var(--body-)',
    color: 'var(--red)',
    margin: '20px 0 0',
    '& a': {
      color: 'var(--red)',
      fontWeight: 'normal',
      textDecoration: 'underline'
    }
  },
  success: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 40px 40px',
    textAlign: 'center',
    height: '100vh',
    fontSize: '16px',
    lineHeight: '1.2',
    '& h2': {
      ...FONT_SIZES.HEADING_LARGE.STYLE,
      margin: '0 0 20px'
    },
    '& p': {
      margin: '0 0 20px'
    },
    '& span': {
      fontSize: '12px'
    },
    '& a': {
      color: '#555',
      fontWeight: 'normal',
      textDecoration: 'underline'
    }
  },
  signIn: {
    margin: '20px -20px 0',
    padding: '32px 0',
    ...FONT_SIZES.BODY_DEFAULT.STYLE,
    borderTop: '1px solid #ececec',
    '& a': {
      color: 'var(--dark-blue)',
      '&:hover, &:focus': {
        color: 'var(--dark-blue-)'
      }
    }
  },
  note: {
    marginTop: '12px',
    display: 'block'
  },
  footerNote: {
    fontSize: '18px',
    textAlign: 'center',
    lineHeight: '24px'
  }
}))(({ classes }) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(false)
  const [success, setSuccess] = useState(false)
  const errorCode = useMemo(
    () => {
      return error && error.data.errors && error.data.errors[0].err
    },
    [error]
  )
  const origin = useOrigin()
  const isParentSignup =
    new URLSearchParams(useLocation().search).get('from') === 'parent'

  return success ? (
    <div className={classes.success}>
      <h2>You’re almost there…</h2>
      <p>
        We’ve sent you a confirmation email.
        <br />
        Please click the link in the email to confirm your Pobble account.
      </p>
      <span>
        You should receive the link within 2 minutes.
        <br />
        If not, please check your spam folder or try{' '}
        <Link to="/forgot-password">resetting your password</Link> in case your
        account already exists.
        <br />
        If you need help, please email{' '}
        <a href="mailto:hello@pobble.com">hello@pobble.com</a>.
      </span>
    </div>
  ) : (
    <Auth className={classes.container}>
      {!isParentSignup && (
        <div className={classes.intro}>
          <h2 className={classes.introTitle}>Create your free account</h2>
          <p className={classes.introDesc}>No payment details needed</p>
        </div>
      )}
      <div className={classes.layoutForm}>
        <div className={classes.signupBox}>
          <Oauth />
          <p className={classes.divider}>
            <span>or</span>
          </p>
          <form
            onSubmit={e => {
              e.preventDefault()
              if (pending) return false
              setPending(true)
              apiEmailSignUp({
                email: email,
                signup_account_type_decision: 'teacher',
                signup_origin: origin
              })
                .then(res => {
                  EVENTS.onAfterFormSignup({
                    type: 'teacher',
                    email: email,
                    res
                  })
                  setSuccess(true)
                })
                .catch(e => {
                  setError(e.response)
                  setPending(false)
                  const code =
                    e.response.data.errors && e.response.data.errors[0].err
                  if (code !== 'e0014' && code !== 'e0054') errorHandler(e)
                })
            }}
            className={classes.form}>
            <FormRow>
              <FormLabel>Email address</FormLabel>
              <FormInput
                name="email"
                value={email}
                onChange={e => {
                  !!error && setError(null)
                  setEmail(e.target.value)
                }}
                required
                type="email"
                center
              />
            </FormRow>
            <FormRow>
              <Button
                className="metrics__sign-up"
                pending={pending}
                wide
                type="submit"
                tall>
                Get started
              </Button>
              <small className={classes.note}>
                We'll send a quick email to activate your account
              </small>
            </FormRow>
            {errorCode === 'e0014' && (
              <p className={classes.error}>
                Sorry, this email is already taken. If you think it's yours,
                please request to{' '}
                <Link to="/forgot-password">reset your password</Link>
              </p>
            )}
            {errorCode === 'e0054' && (
              <p className={classes.error}>
                This email address is not valid or mistyped.
              </p>
            )}
          </form>
          <div className={classes.signIn}>
            Already have an account? <Link to="/">Sign in</Link>
          </div>
        </div>
        <p className={classes.footerNote}>
          Made by teachers, trusted by over a million educators in 70+ countries
        </p>
      </div>
    </Auth>
  )
})

export default Signup
